
import type {
  CurationToolRuleDTO,
  CurationToolRuleType,
  NextQueries,
  ResultService,
  ResultServiceGetParams,
} from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { NextQueriesMessages, RegularFilter } from '../../models'
import type { Action, Tooltip } from '../shared/epb-pill'
import Sortable from 'sortablejs'
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue'
import { useInstance } from '../../composables'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { ColorEnum } from '../../enums'
import { FiltersUtils } from '../../utils/filters.utils'
import EditableLabel from '../editable-label.component.vue'
import FadingScroll from '../fading-scroll.component.vue'
import EpbLegend from '../legend.component.vue'
import { EpbButton } from '../shared/epb-button'
import { EpbPill } from '../shared/epb-pill'
import { EpbSwitch } from '../shared/epb-switch'
import { EpbText } from '../shared/epb-text'

interface Pill {
  key: string
  label?: string
  icon?: string
  actions?: Array<Action>
  color?: ColorEnum | string
  tooltip?: Tooltip
  collapsible?: boolean
  collapsed?: boolean
  loading?: boolean
  disabled?: boolean
  draggable?: boolean
}

interface AddForm {
  key: string
  query: string
  collapsed: boolean
  loading: boolean
}

interface ValidityState {
  type: ValidityStateTypeEnum
  message?: string
}

enum ValidityStateTypeEnum {
  VALID = 'valid',
  ERROR = 'error',
  WARNING = 'warning',
}

export default defineComponent({
  name: 'NextQueriesConfig',
  components: {
    EpbSwitch,
    EpbText,
    EpbPill,
    EditableLabel,
    EpbLegend,
    EpbButton,
    FadingScroll,
  },
  props: {
    value: {
      type: Object as PropType<NextQueries>,
      required: true,
    },
    messages: {
      type: Object as PropType<NextQueriesMessages>,
      required: true,
    },
    pivots: {
      type: Object as PropType<Record<string, RegularFilter>>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    allowDisableAllIndexed: Boolean,
  },
  setup(props, { emit }) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const icons = ref({
      add: 'usercirclesolid',
    })

    const baseClass = 'epb-next-queries-config'

    const pillsList = ref()

    const pillRefs = ref({})

    const addForm = ref(getInitialAddForm())

    function getInitialAddForm(): AddForm {
      return {
        key: Date.now().toString(),
        query: undefined,
        collapsed: true,
        loading: false,
      }
    }

    const addFormQuery = ref()

    const addedPillIds = ref<Record<string, string>>({})

    const searchResults = ref({})

    const pillsListSortableObject = ref(undefined)

    const hideRemovedIndexed = ref(false)

    const addFormPill = computed<Pill>(() => {
      if (props.readonly) {
        return
      }
      const query = addForm.value.query
      const validityState = getRuleValidityState({
        id: undefined,
        type: 'ADD',
        query: query?.trim(),
      })
      return {
        key: addForm.value.key,
        label: query,
        icon: getPillValidityIcon(validityState.type),
        color: getPillValidityColor(validityState.type),
        tooltip: getPillTooltip(validityState.message),
        collapsible: true,
        collapsed: addForm.value.collapsed,
        loading: addForm.value.loading,
      }
    })

    const addedPills = computed<Array<Pill>>(() => {
      return getRulesByType('ADD').map(rule => {
        const validityState = getRuleValidityState(rule, false)
        return {
          key: addedPillIds.value[rule.query] ?? rule.query,
          label: rule.query,
          icon: getPillValidityIcon(validityState.type) ?? icons.value.add,
          ...(!props.readonly && { actions: [{ id: 'discard', icon: 'close' }] }),
          color: getPillValidityColor(validityState.type),
          tooltip: getPillTooltip(validityState.message),
          draggable: !props.readonly,
        }
      })
    })

    function getRulesByType(type: CurationToolRuleType): Array<CurationToolRuleDTO> {
      return props.value.data.rules.filter(rule => rule.type === type)
    }

    function getRuleValidityState(
      rule: CurationToolRuleDTO,
      validateDuplicated = true,
    ): ValidityState {
      if (!rule.query) {
        return {
          type: ValidityStateTypeEnum.VALID,
        }
      }
      const validationMessages = props.messages.fields.rules.validations
      if (isRuleContainingQuery(rule)) {
        return {
          type: ValidityStateTypeEnum.ERROR,
          message: $epb.utils.compileMessage(validationMessages.containsQuery, {
            query: rule.query,
          }),
        }
      }
      if (validateDuplicated && isRuleDuplicated(rule)) {
        return {
          type: ValidityStateTypeEnum.ERROR,
          message: validationMessages.duplicated,
        }
      }
      if (isRuleWithoutResults(rule)) {
        return {
          type: ValidityStateTypeEnum.ERROR,
          message: validationMessages.withoutResults,
        }
      }
      if (isRuleMatchingIndexed(rule)) {
        return {
          type: ValidityStateTypeEnum.WARNING,
          message: validationMessages.matchesIndexed,
        }
      }
      return {
        type: ValidityStateTypeEnum.VALID,
      }
    }

    function isRuleContainingQuery(rule: CurationToolRuleDTO): boolean {
      return props.value.criteria.queries.includes(rule.query)
    }

    function isRuleDuplicated(rule: CurationToolRuleDTO): boolean {
      return props.value.data.rules.some(
        ({ type, query }) => query === rule.query && type === rule.type,
      )
    }

    function isRuleWithoutResults(rule: CurationToolRuleDTO): boolean {
      const results = searchResults.value[rule.query]
      return !!results && !results.length
    }

    function isRuleMatchingIndexed(rule: CurationToolRuleDTO): boolean {
      return props.value.data.indexed.some(indexed => indexed.query === rule.query)
    }

    function getPillValidityIcon(type: ValidityStateTypeEnum): string | undefined {
      switch (type) {
        case ValidityStateTypeEnum.ERROR:
          return 'alert'
        default:
          return undefined
      }
    }

    function getPillValidityColor(type: ValidityStateTypeEnum): ColorEnum | string {
      switch (type) {
        case ValidityStateTypeEnum.ERROR:
          return ColorEnum.ERROR
        case ValidityStateTypeEnum.WARNING:
          return 'ocean'
        default:
          return 'brand'
      }
    }

    function getPillTooltip(message: string): Tooltip {
      return {
        message,
        boundary: 'window',
      }
    }

    const indexedPills = computed<Array<Pill>>(() => {
      return props.value.data.indexed.reduce<Array<Pill>>((result, indexed) => {
        if (!hasRule(indexed.query, props.value.data.rules)) {
          result.push({
            key: indexed.query,
            label: indexed.query,
            draggable: !props.readonly,
            ...(!props.readonly && { actions: [{ id: 'remove', icon: 'close' }] }),
          })
        }
        return result
      }, [])
    })

    const removedPills = computed<Array<Pill>>(() => {
      return getRulesByType('REMOVE').reduce<Array<Pill>>((result, rule) => {
        const hasRule = addedPills.value.find(r => r.label === rule.query)
        if (!hasRule) {
          result.push({
            key: rule.query,
            label: rule.query,
            ...(!props.readonly && { actions: [{ id: 'recover', icon: 'replay' }] }),
            color: 'inactive',
          })
        } else {
          const indexRemoveTag = props.value.data.rules.findIndex(
            r => r.query === hasRule.label && r.type === 'REMOVE',
          )
          if (indexRemoveTag >= 0) {
            props.value.data.rules.splice(indexRemoveTag, 1)
          }
        }
        return result
      }, [])
    })

    const pills = computed<Array<Pill>>(() => {
      return [
        ...addedPills.value,
        ...indexedPills.value,
        ...(hideRemovedIndexed.value ? [] : removedPills.value),
      ]
    })

    const legendItems = computed<Array<{ label: string; icon: string }>>(() => {
      return [
        {
          label: props.messages.fields.rules.types.add,
          icon: icons.value.add,
          color: 'primary',
        },
      ]
    })

    const areAllIndexedDisabled = computed<boolean>(() => {
      return indexedPills.value.every(pill => hasRule(pill.label, props.value.data.rules))
    })

    const hideRemovedIndexedActionMessage = computed<string>(() => {
      const actionMessages = props.messages.fields.rules.actions.remove
      return hideRemovedIndexed.value ? actionMessages.show : actionMessages.hide
    })

    function hasRule(query: string, rules: CurationToolRuleDTO[]): boolean {
      return rules.some(rule => rule.query === query)
    }

    function disableAllIndexed(): void {
      let rules = [...props.value.data.rules]
      if (!areAllIndexedDisabled.value) {
        indexedPills.value.forEach(pill => {
          if (!hasRule(pill.label, rules)) {
            rules.push({
              id: undefined,
              type: 'REMOVE',
              query: pill.label,
            })
          }
        })
      } else {
        rules = rules.filter(rule => rule.type !== 'REMOVE')
      }
      updateRules(rules)
    }

    function remove(query: string): void {
      const rules = [...props.value.data.rules]
      const ruleIndex = rules.findIndex(rule => rule.query === query)
      if (ruleIndex < 0) {
        rules.push({
          id: undefined,
          type: 'REMOVE',
          query,
        })
      } else {
        rules.splice(ruleIndex, 1)
      }
      updateRules(rules)
    }

    function updateRules(rules: Array<CurationToolRuleDTO>): void {
      emit('input', {
        ...props.value,
        data: {
          ...props.value.data,
          rules,
        },
      })
    }

    function toggleCollapsedPill(): void {
      addForm.value.collapsed = !addForm.value.collapsed
      if (!addForm.value.collapsed) {
        addForm.value.query = undefined
        nextTick(() => {
          // nextTick needed because the ref is rendered inside a v-if conditional
          addFormQuery.value.focus()
        })
      }
    }

    const resultService = CONTAINER.get<ResultService>(TYPES.ResultService)
    const { instanceConfig } = useInstance()

    async function add(query: string): Promise<void> {
      query = query?.trim()
      if (!query) {
        return
      }
      const rule: CurationToolRuleDTO = {
        id: undefined,
        type: 'ADD',
        query,
      }
      const validityState = getRuleValidityState(rule)
      if (validityState.type === ValidityStateTypeEnum.ERROR) {
        showPillTooltip(addForm.value.key)
        return
      } else if (!searchResults.value.hasOwnProperty(query)) {
        addForm.value.loading = true
        try {
          const filters = FiltersUtils.getClientFilters(props.pivots, instanceConfig.value)

          const params: ResultServiceGetParams = {
            query,
            filters,
            page: {
              start: 0,
              rows: 1,
            },
          }

          const response = await resultService.get(params)
          const results = response.results
          searchResults.value[query] = results
          if (!results.length) {
            nextTick(() => {
              showPillTooltip(addForm.value.key)
            })
            return
          }
        } catch (error) {
        } finally {
          addForm.value.loading = false
        }
      }
      const rules = [...props.value.data.rules]
      rules.unshift(rule)
      addedPillIds.value[query] = addForm.value.key
      addForm.value = getInitialAddForm()
      updateRules(rules)
    }

    function showPillTooltip(pillKey: string, durationInMillis = 3000): void {
      pillRefs.value[pillKey].showTooltip(durationInMillis)
    }

    function hasConvertPreviousPillToCurated(
      index: number,
      newIndex: number,
      oldIndex: number,
    ): boolean {
      return (index < newIndex && index !== oldIndex) || (index === newIndex && index >= oldIndex)
    }

    onMounted((): void => {
      pillsListSortableObject.value = Sortable.create(pillsList.value.$el, {
        animation: 500,
        draggable: `.${baseClass}__pill-wrapper--draggable`,
        onUpdate: event => {
          const { oldIndex, newIndex } = event
          const rules = [...props.value.data.rules]
          pills.value.forEach((pill, index) => {
            if (
              !pill?.icon &&
              !!pill?.label &&
              (oldIndex === index || hasConvertPreviousPillToCurated(index, newIndex, oldIndex))
            ) {
              const newCuratedPill: CurationToolRuleDTO = {
                id: undefined,
                type: 'ADD',
                query: pill.label,
              }
              rules.splice(newIndex !== 1 ? oldIndex + index : newIndex - 1, 0, newCuratedPill)
            } else if (oldIndex !== index && !!pill?.label) {
              const draggedQuery = pills.value[index].label
              const draggedRuleIndex = rules.findIndex(rule => rule.query === draggedQuery)
              if (draggedRuleIndex >= 0) {
                rules.splice(index, 0, rules.splice(draggedRuleIndex, 1)[0])
              }
            }
          })

          const draggedQuery = pills.value[oldIndex].label

          const newIndexRule =
            newIndex !== 1 && oldIndex - newIndex === 1
              ? newIndex
              : addedPills.value.length + indexedPills.value.length - 1 <= newIndex
                ? newIndex + 1
                : newIndex - 1

          rules.splice(
            newIndexRule,
            0,
            rules.splice(
              rules.findIndex(rule => rule.query === draggedQuery),
              1,
            )[0],
          )

          updateRules(rules)
        },
      })
    })

    onBeforeUnmount((): void => {
      if (pillsListSortableObject.value?.el) {
        pillsListSortableObject.value.destroy()
      }
    })

    return {
      baseClass,
      pillsList,
      pillRefs,
      hideRemovedIndexed,
      addForm,
      addFormQuery,
      pills,
      legendItems,
      areAllIndexedDisabled,
      hideRemovedIndexedActionMessage,
      addFormPill,
      disableAllIndexed,
      remove,
      toggleCollapsedPill,
      add,
    }
  },
})
